import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://fitpulse.es/api';

interface Coupon {
  id: string;
  code: string;
  discount: number;
  maxUses: number;
  usedCount: number;
  expiresAt: string | null;
  isActive: boolean;
  description: string | null;
  createdAt: string;
  _count?: { uses: number };
}

export function Coupons() {
  const [coupons, setCoupons] = useState<Coupon[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ code: '', discount: 20, maxUses: 10, expiresAt: '', description: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchCoupons(); }, []);

  const fetchCoupons = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const r = await axios.get(`${API_URL}/coupons`, { headers: { Authorization: `Bearer ${token}` } });
      setCoupons(r.data.data.coupons || []);
    } catch { toast.error('No tienes acceso a esta sección'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.code || !form.discount) { toast.error('Rellena el código y descuento'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(`${API_URL}/coupons`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('✅ Cupón creado');
      setShowForm(false);
      setForm({ code: '', discount: 20, maxUses: 10, expiresAt: '', description: '' });
      fetchCoupons();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Error creando cupón');
    } finally { setSaving(false); }
  };

  const toggleActive = async (id: string, isActive: boolean) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.patch(`${API_URL}/coupons/${id}`, { isActive: !isActive }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(isActive ? 'Cupón desactivado' : 'Cupón activado');
      fetchCoupons();
    } catch { toast.error('Error actualizando cupón'); }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Eliminar este cupón?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_URL}/coupons/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Cupón eliminado');
      fetchCoupons();
    } catch { toast.error('Error eliminando cupón'); }
  };

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const code = 'FIT' + Array.from({length: 6}, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    setForm(f => ({ ...f, code }));
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
    </div>
  );

  return (
    <div className="space-y-6">
      <style>{`
        .coupon-card { background: white; border-radius: 16px; border: 1px solid #e2e8f0; padding: 20px; display: flex; align-items: center; gap: 16px; transition: box-shadow .2s; }
        .coupon-card:hover { box-shadow: 0 4px 16px rgba(0,0,0,.08); }
        .coupon-code { background: linear-gradient(135deg, #1e293b, #334155); color: white; font-family: monospace; font-size: 1.1rem; font-weight: 800; padding: 10px 18px; border-radius: 10px; letter-spacing: .1em; flex-shrink: 0; }
        .coupon-disc { width: 52px; height: 52px; background: linear-gradient(135deg, #3b82f6, #6366f1); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: 900; font-size: .85rem; flex-shrink: 0; text-align: center; line-height: 1.1; }
        .toggle { position: relative; width: 44px; height: 24px; background: #e2e8f0; border-radius: 100px; cursor: pointer; transition: background .3s; border: none; flex-shrink: 0; }
        .toggle.on { background: #10b981; }
        .toggle-knob { position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; background: white; border-radius: 50%; transition: transform .3s; box-shadow: 0 1px 3px rgba(0,0,0,.2); }
        .toggle.on .toggle-knob { transform: translateX(20px); }
      `}</style>

      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">🎟️ Cupones de descuento</h1>
          <p className="text-secondary-500 text-sm mt-1">Crea y gestiona cupones para las suscripciones de FitPulse</p>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary flex items-center gap-2">
          {showForm ? '✕ Cancelar' : '+ Nuevo cupón'}
        </button>
      </div>

      {/* FORMULARIO */}
      {showForm && (
        <div className="card p-6">
          <h3 className="text-base font-semibold text-secondary-900 mb-4">Crear nuevo cupón</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">Código *</label>
              <div className="flex gap-2">
                <input className="input flex-1" value={form.code} onChange={e => setForm(f => ({...f, code: e.target.value.toUpperCase()}))} placeholder="FITPULSE20"/>
                <button onClick={generateCode} className="btn-secondary text-sm px-3" title="Generar código">🎲</button>
              </div>
            </div>
            <div>
              <label className="label">Descuento (%)</label>
              <input className="input" type="number" min={1} max={100} value={form.discount} onChange={e => setForm(f => ({...f, discount: parseInt(e.target.value)}))}/>
            </div>
            <div>
              <label className="label">Usos máximos</label>
              <input className="input" type="number" min={1} value={form.maxUses} onChange={e => setForm(f => ({...f, maxUses: parseInt(e.target.value)}))}/>
            </div>
            <div>
              <label className="label">Fecha de expiración</label>
              <input className="input" type="date" value={form.expiresAt} onChange={e => setForm(f => ({...f, expiresAt: e.target.value}))}/>
            </div>
            <div className="md:col-span-2">
              <label className="label">Descripción (opcional)</label>
              <input className="input" value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} placeholder="Ej: Descuento de bienvenida para nuevos negocios"/>
            </div>
          </div>

          {/* Preview */}
          {form.code && (
            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 16, marginTop: 16, display: 'flex', alignItems: 'center', gap: 14 }}>
              <div className="coupon-code">{form.code}</div>
              <div>
                <p style={{ fontWeight: 700, color: '#1e293b' }}>{form.discount}% de descuento</p>
                <p style={{ fontSize: '.78rem', color: '#64748b' }}>Válido para {form.maxUses} uso{form.maxUses !== 1 ? 's' : ''}{form.expiresAt ? ` · Expira ${new Date(form.expiresAt).toLocaleDateString('es-ES')}` : ''}</p>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setShowForm(false)} className="btn-secondary">Cancelar</button>
            <button onClick={handleCreate} disabled={saving} className="btn-primary">
              {saving ? 'Creando...' : '✅ Crear cupón'}
            </button>
          </div>
        </div>
      )}

      {/* STATS */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-secondary-900">{coupons.length}</p>
          <p className="text-xs text-secondary-400 mt-1">Total cupones</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{coupons.filter(c => c.isActive).length}</p>
          <p className="text-xs text-secondary-400 mt-1">Activos</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{coupons.reduce((s, c) => s + c.usedCount, 0)}</p>
          <p className="text-xs text-secondary-400 mt-1">Total usos</p>
        </div>
        <div className="card p-4 text-center">
          <p className="text-2xl font-bold text-secondary-900">{coupons.filter(c => c.expiresAt && new Date(c.expiresAt) < new Date()).length}</p>
          <p className="text-xs text-secondary-400 mt-1">Expirados</p>
        </div>
      </div>

      {/* LISTA */}
      {coupons.length > 0 ? (
        <div className="space-y-3">
          {coupons.map(c => {
            const expired = c.expiresAt && new Date(c.expiresAt) < new Date();
            const exhausted = c.usedCount >= c.maxUses;
            return (
              <div key={c.id} className="coupon-card" style={{ opacity: !c.isActive || expired ? .6 : 1 }}>
                <div className="coupon-code">{c.code}</div>
                <div className="coupon-disc">{c.discount}%<br/><span style={{fontSize:'.6rem'}}>OFF</span></div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: '.92rem', color: '#1e293b' }}>{c.discount}% descuento</span>
                    {expired && <span style={{ background: '#fee2e2', color: '#dc2626', fontSize: '.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100 }}>Expirado</span>}
                    {exhausted && <span style={{ background: '#f1f5f9', color: '#64748b', fontSize: '.65rem', fontWeight: 700, padding: '2px 8px', borderRadius: 100 }}>Agotado</span>}
                  </div>
                  {c.description && <p style={{ fontSize: '.78rem', color: '#64748b', marginBottom: 4 }}>{c.description}</p>}
                  <div style={{ display: 'flex', gap: 12, fontSize: '.72rem', color: '#94a3b8' }}>
                    <span>👥 {c.usedCount}/{c.maxUses} usos</span>
                    {c.expiresAt && <span>📅 Expira {new Date(c.expiresAt).toLocaleDateString('es-ES')}</span>}
                    <span>📆 Creado {new Date(c.createdAt).toLocaleDateString('es-ES')}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <button className={`toggle ${c.isActive ? 'on' : ''}`} onClick={() => toggleActive(c.id, c.isActive)}>
                    <div className="toggle-knob"></div>
                  </button>
                  <button onClick={() => handleDelete(c.id)} style={{ padding: '8px 10px', borderRadius: 8, background: '#fef2f2', color: '#ef4444', border: 'none', cursor: 'pointer' }}>
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <div style={{ fontSize: '3rem', marginBottom: 12 }}>🎟️</div>
          <p className="text-secondary-500 font-medium mb-2">Sin cupones todavía</p>
          <p className="text-secondary-400 text-sm mb-4">Crea tu primer cupón de descuento</p>
          <button onClick={() => setShowForm(true)} className="btn-primary">+ Crear cupón</button>
        </div>
      )}
    </div>
  );
}
